import { Button } from '@/components/ui/button';
import { useSystemNotifications } from '@/hooks/use-system-notifications';
import { cn } from '@/lib/utils';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { Link } from 'react-router';

const severityStyles = {
  info: {
    banner: 'bg-sky-50 text-sky-950 dark:bg-sky-950 dark:text-sky-50',
    icon: 'text-sky-600 dark:text-sky-400',
    button: 'text-sky-900 hover:bg-sky-200/70 hover:text-sky-950 dark:text-sky-100 dark:hover:bg-sky-900',
    Icon: Info,
  },
  warning: {
    banner: 'bg-amber-50 text-amber-950 dark:bg-amber-950 dark:text-amber-50',
    icon: 'text-amber-600 dark:text-amber-400',
    button: 'text-amber-900 hover:bg-amber-200/70 hover:text-amber-950 dark:text-amber-100 dark:hover:bg-amber-900',
    Icon: AlertTriangle,
  },
  error: {
    banner: 'bg-red-50 text-red-950 dark:bg-red-950 dark:text-red-50',
    icon: 'text-red-600 dark:text-red-400',
    button: 'text-red-900 hover:bg-red-200/70 hover:text-red-950 dark:text-red-100 dark:hover:bg-red-900',
    Icon: AlertCircle,
  },
  success: {
    banner: 'bg-emerald-50 text-emerald-950 dark:bg-emerald-950 dark:text-emerald-50',
    icon: 'text-emerald-600 dark:text-emerald-400',
    button:
      'text-emerald-900 hover:bg-emerald-200/70 hover:text-emerald-950 dark:text-emerald-100 dark:hover:bg-emerald-900',
    Icon: CheckCircle2,
  },
} as const;

export function SystemNotificationBanner() {
  const { notifications, dismissNotification } = useSystemNotifications();

  if (notifications.length === 0) return null;

  return (
    <section aria-label="Avisos importantes do sistema" className="grid gap-px">
      {notifications.map((notification) => {
        const styles = severityStyles[notification.severity ?? 'warning'];
        const Icon = styles.Icon;

        return (
          <div
            key={notification.id}
            role="alert"
            className={cn('flex min-h-9 items-center gap-2 border-b px-4 py-1.5', styles.banner)}
          >
            <Icon aria-hidden="true" className={cn('size-4 shrink-0', styles.icon)} />
            <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-1.5 text-xs sm:text-sm">
              {notification.title && <span className="font-semibold">{notification.title}.</span>}
              <span className="text-current/80">{notification.message}</span>
              {notification.action && (
                <Link
                  to={notification.action.href}
                  className="inline-flex font-semibold text-current underline decoration-current/40 underline-offset-4 transition-colors hover:decoration-current"
                >
                  {notification.action.label}
                </Link>
              )}
            </div>
            {notification.dismissible !== false && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn('-my-1 -mr-1 size-7 shrink-0', styles.button)}
                aria-label={`Dispensar aviso${notification.title ? `: ${notification.title}` : ''}`}
                onClick={() => dismissNotification(notification.id)}
              >
                <X aria-hidden="true" className="size-3.5" />
              </Button>
            )}
          </div>
        );
      })}
    </section>
  );
}
